import { ReactNode } from "react";

import { ImageIcon, UserIcon } from "lucide-react";

import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type Poster = { url?: string | null; blurhash?: string | null } | null;

/**
 * Media-forward header for a show page: an optional poster/avatar alongside the
 * title, an eyebrow, a meta line, badge groups and a description. A blurred copy
 * of the poster bleeds behind the card to give the detail view some atmosphere
 * without leaving the design system.
 */
export function ShowHero({
  poster,
  round,
  eyebrow,
  title,
  subtitle,
  meta,
  badges,
  description,
}: {
  poster?: Poster;
  /** Render the poster as a circular avatar (people). */
  round?: boolean;
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  badges?: ReactNode;
  description?: ReactNode;
}) {
  const url = poster?.url ?? undefined;

  return (
    <Card className="relative overflow-hidden">
      {url && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `url(${url})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(36px)",
          }}
        />
      )}
      <div className="relative flex flex-col gap-6 p-6 sm:flex-row">
        <div className="shrink-0">
          {url ? (
            <PhotoWithBlurhash
              url={url}
              blurhash={poster?.blurhash ?? ""}
              className={cn(
                "bg-muted object-cover shadow-sm ring-1 ring-border",
                round
                  ? "size-28 rounded-full"
                  : "h-44 w-28 rounded-lg sm:h-52 sm:w-36",
              )}
            />
          ) : (
            <div
              className={cn(
                "flex items-center justify-center bg-muted text-muted-foreground ring-1 ring-border",
                round
                  ? "size-28 rounded-full"
                  : "h-44 w-28 rounded-lg sm:h-52 sm:w-36",
              )}
            >
              {round ? (
                <UserIcon className="size-8" />
              ) : (
                <ImageIcon className="size-8" />
              )}
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {eyebrow && (
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {eyebrow}
            </div>
          )}
          <div className="space-y-1">
            <h1 className="text-2xl font-bold leading-tight tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          {meta && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              {meta}
            </div>
          )}
          {badges && (
            <div className="flex flex-wrap items-center gap-2">{badges}</div>
          )}
          {description && (
            <p className="max-w-prose whitespace-pre-line text-sm leading-relaxed text-foreground/90">
              {description}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Small dot separator for the hero meta line. */
export function MetaDot() {
  return <span className="text-muted-foreground/50">·</span>;
}
